import { pickedDay } from "@/lib/clinic-clock";
import {
  tzOffsetMs,
  zonedDateKey,
  zonedDayStartOfInstant,
  zonedInstantOnDay,
  zonedWallMinutes,
} from "@/lib/timezone-dates";

/**
 * `instant`'s wall time in `timeZone` on `day`, a date picker's marker.
 * Re-picking the day it is already on returns it unchanged, so the second of
 * a repeated fall-back hour isn't folded into the first.
 */
export function wallTimeOnPickedDay(
  instant: Date,
  day: Date,
  timeZone: string,
): Date {
  if (zonedDateKey(instant, timeZone) === pickedDay(day)) return instant;
  return zonedInstantOnDay(day, zonedWallMinutes(instant, timeZone), timeZone);
}

/**
 * Where a span sits on an hour grid: wall-clock minutes from `midnight` (a
 * midnight in `timeZone`; the start's own by default) to its start and end.
 * Not wrapped at 1440, so a span into the next day reads past it. The end is
 * its own wall time — a span over the spring-forward gap reaches the row its
 * end is on — but never sooner than the time that passed, so a span over the
 * fall-back fold keeps its real, positive length.
 */
export function wallSpanMinutes(
  start: string | Date,
  end: string | Date,
  timeZone: string,
  midnight: Date = zonedDayStartOfInstant(start, timeZone),
): { start: number; end: number } {
  const wallMinutes = (instant: string | Date) =>
    (new Date(instant).getTime() -
      midnight.getTime() +
      tzOffsetMs(timeZone, instant) -
      tzOffsetMs(timeZone, midnight)) /
    60_000;
  const startMinutes = wallMinutes(start);
  const elapsedMinutes =
    (new Date(end).getTime() - new Date(start).getTime()) / 60_000;
  return {
    start: startMinutes,
    end: Math.max(wallMinutes(end), startMinutes + elapsedMinutes),
  };
}
